(() => {
  class StockFirstInfiniteScroll {
    constructor(productList) {
      this.productList = productList;
      this.sectionId =
        productList.dataset.sectionId ||
        productList.id.replace(/^product-list-/, '');

      this.phase = 'in-stock';

      this.nextUrl = null;

      this.loading = false;
      this.finished = false;
      this.initialized = false;

      // Change this if your theme uses a different product-card selector.
      this.productCardSelector = '.product-card';

      this.originalHTML = productList.innerHTML;

      this.init();
    }

    init() {
      // Do not run if customer has explicitly selected
      // an availability filter.
      const currentUrl = new URL(window.location.href);

      if (currentUrl.searchParams.has('filter.v.availability')) {
        return;
      }

      this.createSentinel();

      this.loadInitialProducts();
    }

    createSentinel() {
      const section =
        this.productList.closest('.collection__main') ||
        this.productList.closest('.shopify-section') ||
        this.productList.parentElement;

      this.pagination = section
        ? section.querySelector('nav.pagination')
        : null;

      if (!this.pagination) {
        this.pagination = document.createElement('nav');
        this.pagination.className = 'pagination stock-first-pagination';

        this.productList.after(this.pagination);
      }

      this.pagination.classList.add('stock-first-hidden');
      this.pagination.setAttribute('aria-hidden', 'true');

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) {
            return;
          }

          this.loadNext();
        },
        {
          rootMargin: '800px 0px',
        }
      );

      this.observer.observe(this.pagination);
    }

    getPageUrl(availability, page = null) {
      const url = new URL(window.location.href);

      // Keep all existing filters, sorting, search parameters, etc.
      url.searchParams.set(
        'filter.v.availability',
        availability
      );

      if (page) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }

      // Request only the collection section.
      url.searchParams.set(
        'section_id',
        this.sectionId
      );

      return url;
    }

    async fetchSection(url) {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Collection request failed: ${response.status}`
        );
      }

      const html = await response.text();

      const parser = new DOMParser();
      const documentFragment =
        parser.parseFromString(html, 'text/html');

      const incomingProductList =
        documentFragment.querySelector(
          `product-list#${CSS.escape(this.productList.id)}`
        );

      if (!incomingProductList) {
        throw new Error(
          'Product list was not found in Shopify section response.'
        );
      }

      const nextLink =
        documentFragment.querySelector(
          'nav.pagination a[rel="next"],' +
          'nav.pagination .next a,' +
          'nav.pagination a[aria-label*="Next" i]'
        );

      return {
        productList: incomingProductList,
        nextUrl: nextLink
          ? new URL(nextLink.href, window.location.origin)
          : null,
      };
    }

    getProductCards(productList) {
      return [
        ...productList.querySelectorAll(
          this.productCardSelector
        ),
      ];
    }

    appendProducts(incomingProductList) {
      const products =
        this.getProductCards(incomingProductList);

      if (!products.length) {
        return 0;
      }

      const fragment =
        document.createDocumentFragment();

      products.forEach((product) => {
        fragment.appendChild(
          document.importNode(product, true)
        );
      });

      this.productList.appendChild(fragment);

      return products.length;
    }

    clearCurrentProducts() {
      this.getProductCards(this.productList).forEach(
        (product) => product.remove()
      );
    }

    async loadInitialProducts() {
      if (this.loading || this.initialized) {
        return;
      }

      this.loading = true;

      this.productList.classList.add(
        'stock-first-loading'
      );

      try {
        const url = this.getPageUrl('1');

        const result =
          await this.fetchSection(url);

        const count =
          this.getProductCards(
            result.productList
          ).length;

        // Remove the originally rendered mixed-stock products.
        this.clearCurrentProducts();

        // Add first in-stock page.
        this.appendProducts(
          result.productList
        );

        this.nextUrl = result.nextUrl;

        this.initialized = true;

        this.productList.classList.remove(
          'stock-first-loading'
        );

        // No more in-stock pages.
        // Start out-of-stock stream.
        if (!this.nextUrl && count === 0) {
          await this.switchToOutOfStock();
        } else if (!this.nextUrl) {
          await this.switchToOutOfStock();
        }

      } catch (error) {
        console.error(
          '[Stock First Infinite Scroll]',
          error
        );

        // If our custom loading fails,
        // restore the original collection.
        this.productList.innerHTML =
          this.originalHTML;

        this.productList.classList.remove(
          'stock-first-loading'
        );

        this.finished = true;
      } finally {
        this.loading = false;
      }
    }

    async loadNext() {
      if (
        this.loading ||
        this.finished ||
        !this.initialized
      ) {
        return;
      }

      this.loading = true;

      try {
        // ------------------------------------
        // MORE IN-STOCK PRODUCTS
        // ------------------------------------
        if (this.phase === 'in-stock') {
          if (!this.nextUrl) {
            await this.switchToOutOfStock();
            return;
          }

          const result =
            await this.fetchSection(
              this.nextUrl
            );

          this.appendProducts(
            result.productList
          );

          this.nextUrl =
            result.nextUrl;

          // In-stock products finished.
          if (!this.nextUrl) {
            await this.switchToOutOfStock();
          }

          return;
        }

        // ------------------------------------
        // MORE OUT-OF-STOCK PRODUCTS
        // ------------------------------------
        if (this.phase === 'out-of-stock') {
          if (!this.nextUrl) {
            this.finished = true;

            this.observer.disconnect();

            return;
          }

          const result =
            await this.fetchSection(
              this.nextUrl
            );

          this.appendProducts(
            result.productList
          );

          this.nextUrl =
            result.nextUrl;

          if (!this.nextUrl) {
            this.finished = true;

            this.observer.disconnect();
          }
        }

      } catch (error) {
        console.error(
          '[Stock First Infinite Scroll]',
          error
        );
      } finally {
        this.loading = false;
      }
    }

    async switchToOutOfStock() {
      if (this.phase === 'out-of-stock') {
        return;
      }

      this.phase = 'out-of-stock';

      const url =
        this.getPageUrl('0');

      const result =
        await this.fetchSection(url);

      this.appendProducts(
        result.productList
      );

      this.nextUrl =
        result.nextUrl;

      if (!this.nextUrl) {
        this.finished = true;

        if (this.observer) {
          this.observer.disconnect();
        }
      }
    }
  }

  function initStockFirst() {
    const productList =
      document.querySelector(
        'product-list.product-list[data-stock-first="true"]'
      );

    if (!productList) {
      return;
    }

    if (
      productList.dataset.stockFirstInitialized ===
      'true'
    ) {
      return;
    }

    productList.dataset.stockFirstInitialized =
      'true';

    new StockFirstInfiniteScroll(productList);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initStockFirst,
      { once: true }
    );
  } else {
    initStockFirst();
  }

  /*
   * Useful when Shopify/theme filtering replaces
   * the collection section through AJAX.
   */
  document.addEventListener(
    'shopify:section:load',
    initStockFirst
  );
})();